import { useGetAccountsQuery } from "@/modules/account/services";
import { ApplicationKeys } from "@/modules/application/common/enum";
import type {
  IPeriodicalPaymentsApplicationForm,
  IPeriodicalPaymentsApplicationRequest
} from "@/modules/application/common/types";
import {
  ApplicationTermsAndConditionsLink
} from "@/modules/application/components/ApplicationTermsAndConditionsLink";
import { getWorkingDayAdjustmentOptions } from "@/modules/application/helpers";
import { getBeneficiaryBankOptions } from "@/modules/application/helpers/optionHelpers";
import {
  useCreatePeriodicalPaymentsApplicationMutation,
  useGetPeriodicalPaymentsApplicationQuery,
  useUpdatePeriodicalPaymentsApplicationMutation
} from "@/modules/application/services";
import { DATE_FORMAT, INITIAL_PAGE_SIZE, INPUT_MAX_LIMIT } from "@/shared/common/constants";
import { BeneficiaryType } from "@/shared/common/enums";
import type { IExtendedObject, IOperationPageProps } from "@/shared/common/types";
import { OwnAccountSelectOption, Select, TransferBeneficiaries, } from "@/shared/components";
import { ApplicationLayout } from "@/shared/components/ApplicationLayout";
import {
  cleanupAmount,
  formatDate,
  isAmeriaAccount,
  isValidAccount,
  isValidAmount,
  renderSelectedAccount
} from "@/shared/helpers";
import { useGetAccountHolder, useHasFormChanges } from "@/shared/hooks";
import { debounce } from "@/shared/utils";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { SimpleDatePicker } from "@ab.uitools/ui-library/components/DatePicker";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props extends IOperationPageProps {
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

export const PeriodicalPaymentsApplicationForm = ({
  isEdit, isCopy, setIsAccountNumberInvalid
}: Props) => {
  const { t, i18n: { language } } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, setValue, reset, watch } = useFormProps();

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});
  const [beneficiaryName, setBeneficiaryName] = useState<string>("");

  const {
    accountData, getAccountInfo, clearAccountData
  } = useGetAccountHolder({ setIsAccountNumberInvalid });
  const beneficiaryNameRef = useRef(beneficiaryName);
  beneficiaryNameRef.current = beneficiaryName;

  const {
    data: accountsList = [],
    isLoading: isGetAccountsLoading,
  } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: periodicalPaymentsApplication,
    isLoading: isGetApplicationLoading,
  } = useGetPeriodicalPaymentsApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const [
    createPeriodicalPaymentsApplicationApi,
    { isLoading: isCreateLoading },
  ] = useCreatePeriodicalPaymentsApplicationMutation();

  const [
    updatePeriodicalPaymentsApplicationApi,
    { isLoading: isUpdateLoading },
  ] = useUpdatePeriodicalPaymentsApplicationMutation();

  const periodicalPaymentsApplicationAction = useCallback((values: IPeriodicalPaymentsApplicationForm) => {
    const requestDate: IPeriodicalPaymentsApplicationRequest = {
      ...values,
      email: values.sendEmailNotification ? values.email : "",
      amount: +cleanupAmount(`${values.amount}`),
      startDate: formatDate(values.startDate, DATE_FORMAT.YYYY_MM_DD),
      endDate: values.endDate ? formatDate(values.endDate, DATE_FORMAT.YYYY_MM_DD) : "",
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount),
    };

    return isEdit ? updatePeriodicalPaymentsApplicationApi({
        data: requestDate,
        id: `${periodicalPaymentsApplication?.id}`,
      }).unwrap() :
      createPeriodicalPaymentsApplicationApi({
        data: requestDate,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, periodicalPaymentsApplication]);

  const values = watch?.() || {};

  const handleChangeBeneficiaryAccount = useMemo(
    () => debounce((account: string) => {
      if (!account || !isValidAccount(account)) {
        setValue(ApplicationKeys.Beneficiary, "");
        clearAccountData();
        return;
      }
      getAccountInfo({
        account,
        setValue,
        setError: () => {},
        getBeneficiaryName: () => beneficiaryNameRef.current,
        creditAccountKey: ApplicationKeys.CreditAccount,
        beneficiaryNameKey: ApplicationKeys.Beneficiary,
      });
    }, 700),
    [],
  );

  const accountsOptions = useMemo(() =>
    accountsList.map((item, index) => ({
      value: item.number,
      label: renderSelectedAccount(item),
      meta: { ...item, index },
    })), [accountsList]);

  const setDefaultValues = () => {
    const data = {
      email: periodicalPaymentsApplication?.email,
      amount: periodicalPaymentsApplication?.amount,
      purpose: periodicalPaymentsApplication?.purpose,
      creditAccount: periodicalPaymentsApplication?.creditAccount,
      beneficiary: periodicalPaymentsApplication?.beneficiary,
      beneficiaryBank: periodicalPaymentsApplication?.beneficiaryBank,
      frequencyInfo: periodicalPaymentsApplication?.frequencyInfo,
      startDate: periodicalPaymentsApplication?.startDate,
      endDate: periodicalPaymentsApplication?.endDate,
      debitAccount: periodicalPaymentsApplication?.debitAccount?.number,
      applicationNumber: isEdit ?
        periodicalPaymentsApplication?.applicationNumber : values.applicationNumber,
      workingDayAdjustment: periodicalPaymentsApplication?.workingDayAdjustment,
      sendEmailNotification: periodicalPaymentsApplication?.sendEmailNotification,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    amount: values.amount,
    creditAccount: values.creditAccount,
    beneficiary: values.beneficiary,
    beneficiaryBank: values.beneficiaryBank,
    frequencyInfo: values.frequencyInfo,
    startDate: values.startDate,
    endDate: values.endDate,
    debitAccount: values.debitAccount,
    workingDayAdjustment: values.workingDayAdjustment,
    sendEmailNotification: values.sendEmailNotification,
    purpose: values.purpose,
    email: values.email,
    applicationNumber: values.applicationNumber,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!periodicalPaymentsApplication?.id) {
      setDefaultValues();
    }
  }, [periodicalPaymentsApplication]);

  return (
    <ApplicationLayout
      title={t("application.periodicalPayments.title")}
      page="Periodical payments application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={periodicalPaymentsApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="debitAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    className="custom-select"
                    options={accountsOptions}
                    isDisabled={isGetAccountsLoading}
                    label={t("application.periodicalPayments.account")}
                    placeholder={t("application.periodicalPayments.chooseClosureAccount")}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="amount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    label={t("general.amount")}
                    placeholder={t("general.enterAmount")}
                    thousandSeparator=","
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}
              />
              <FormField
                name="sendEmailNotification"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    label={t("application.periodicalPayments.sendEmailNotification")}
                  />
                )}
              />
              {values.sendEmailNotification && (
                <FormField
                  name="email"
                  className="mb-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      label={t("application.periodicalPayments.receiveEmail")}
                      placeholder={t("application.periodicalPayments.enterReceiveEmail")}
                    />
                  )}
                />
              )}
              <FormField
                name="purpose"
                className="mb-8 mt-32"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <TransferBeneficiaries
                name="creditAccount"
                className="mb-8"
                setBeneficiaryName={setBeneficiaryName}
                entryType={BeneficiaryType.Account}
                onChange={(v) => handleChangeBeneficiaryAccount(v)}
                label={t("application.periodicalPayments.creditAccount")}
                placeholder={t("application.periodicalPayments.typeOrChooseAnAccount")}
              />
              {accountData?.bankName && (
                <Text className="mt-8" type="tertiary">
                  {accountData?.bankName}
                </Text>
              )}
              {accountData?.holder && isAmeriaAccount(values.account) ? (
                <div className="mt-32">
                  <Text>{t("application.periodicalPayments.accountHolder")}</Text>
                  <Text className="my-8">
                    {accountData?.holder}
                  </Text>
                </div>
              ) : (
                <FormField
                  name="beneficiary"
                  className="mb-32 mt-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      required
                      isTrimValues
                      disabled={isAmeriaAccount(values.creditAccount)}
                      label={t("application.periodicalPayments.beneficiary")}
                      placeholder={t("application.periodicalPayments.enterBeneficiary")}
                    />
                  )}
                />
              )}
              <FormField
                name="beneficiaryBank"
                className="mb-8"
                errorMessageIcon="infoFilled"
                shouldValidate
                As={(props) => (
                  <Select
                    {...props}
                    required
                    options={getBeneficiaryBankOptions({ t })}
                    label={t(
                      "application.periodicalPayments.beneficiaryBank")}
                    placeholder={t("application.periodicalPayments.chooseBeneficiaryBank")}
                  />
                )}
              />
            </>,
            title: t("general.beneficiaryDetails"),
            value: "beneficiary_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="frequencyInfo"
                className="mb-12 mt-40"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.frequencyInfo?.message || ""}`}
                    label={t("application.periodicalPayments.frequency")}
                    placeHolder={t("application.periodicalPayments.enterFrequency")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <FormField
                name="workingDayAdjustment"
                className="mb-32"
                errorMessageIcon="infoFilled"
                shouldValidate
                As={(props) => (
                  <Select
                    {...props}
                    required
                    options={getWorkingDayAdjustmentOptions({ t })}
                    label={t(
                      "application.periodicalPayments.nonWorkingDays")}
                    placeholder={t(
                      "application.periodicalPayments.chooseWorkingDayAdjustment")}
                  />
                )}
              />
              <div className="flexbox mb-32 gap-16">
                <FormField
                  name="startDate"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <SimpleDatePicker
                      {...props}
                      required
                      closeOnScroll
                      dataId="startDate"
                      locale={language}
                      format={DATE_FORMAT.DD_MM_YYYY}
                      label={t("general.startDate")}
                      value={props.value as Date}
                      minDate={new Date()}
                    />
                  )}
                />
                <FormField
                  name="endDate"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <SimpleDatePicker
                      {...props}
                      closeOnScroll
                      dataId="endDate"
                      locale={language}
                      format={DATE_FORMAT.DD_MM_YYYY}
                      label={t("general.endDate")}
                      value={props.value as Date}
                      minDate={new Date()}
                    />
                  )}
                />
              </div>
              <FormField
                name="isAgreeWithConditions"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    isInvalid={!!errors.isAgreeWithConditions}
                    label={<ApplicationTermsAndConditionsLink/>}
                  />
                )}
              />
            </>,
            title: t("application.periodicalPayments.frequencySettings"),
            value: "frequency_settings",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
